import { useTranslation } from 'react-i18next';
import Accordion from 'react-bootstrap/Accordion';
import { Typography } from 'app/components/ui';
import { i18n } from 'shared/i18n';
import { AccordionHeader } from './AccordionHeader';
import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useDocumentParams } from 'app/router/params';
import { AffectedPage, AffectedTag, ListItemsTable, ListItemsTableTag } from './ContentViews/ListItemsTable';
import { TagItemShortcuts } from '../TagItemShortcuts/TagItemShortcuts';
import { FindingDetailsModal, FindingDetails } from './FindingDetailsModal';
import { TagWithMetadata, useTagsByDocument, useDocumentTaxonomyConfig } from 'entities/tag';
import { useDownloadReportPackage } from './useDownloadReportPackage';
import { ValidationCategory, ValidationFinding, useRunValidation } from './useRunValidation';
import { useLatestValidation } from 'app/api/validation-queries';

import './Export.scss';
import { useGetDocumentBySuperId } from 'entities/document';

interface ExportProps {
  documentId: string;
  onTagDetailsClick?: (tag: TagWithMetadata) => void;
}

interface TagInfo {
  pageId: string | null;
  pageNumber: number | null;
  sectionSuperId: string | null;
  sectionName: string;
  conceptQName: string;
}

function buildTagInfoMap(tags: TagWithMetadata[] | undefined): Map<string, TagInfo> {
  const map = new Map<string, TagInfo>();
  if (!tags) return map;
  for (const tag of tags) {
    map.set(tag.id, {
      pageId: tag.pageId,
      pageNumber: tag.metadata?.pageNumber ?? null,
      sectionSuperId: tag.sectionSuperId,
      sectionName: tag.metadata?.sectionName ?? '',
      conceptQName: tag.concept?.name ?? tag.conceptId,
    });
  }
  return map;
}

function buildFactIdMap(tags: TagWithMetadata[] | undefined): Map<string, AffectedPage> {
  const map = new Map<string, AffectedPage>();
  if (!tags) return map;
  for (const tag of tags) {
    map.set(tag.id, {
      pageId: tag.pageId,
      pageNumber: tag.metadata?.pageNumber ?? null,
      sectionSuperId: tag.sectionSuperId,
      sectionName: tag.metadata?.sectionName ?? '',
    });
  }
  return map;
}

function resolveAffectedPages(factIds: string[], factIdMap: Map<string, AffectedPage>): AffectedPage[] {
  const byPageId = new Map<string | null, AffectedPage>();
  for (const factId of factIds) {
    const info = factIdMap.get(factId);
    if (!info) continue;
    const existing = byPageId.get(info.pageId);
    if (existing) {
      existing.count = (existing.count ?? 1) + 1;
    } else {
      byPageId.set(info.pageId, { ...info, count: 1 });
    }
  }
  return Array.from(byPageId.values());
}

function locationLabelFor(pages: AffectedPage[]): string | undefined {
  if (pages.length === 0) return undefined;
  if (pages.length === 1) {
    const p = pages[0];
    return p.pageNumber != null ? `${i18n('all:tags.page')} ${p.pageNumber}` : p.sectionName || i18n('all:tags.page');
  }
  return `${pages.length} ${i18n('all:tags.tags')}`;
}

function resolveAffectedTags(
  factIds: string[],
  tagInfoMap: Map<string, TagInfo>,
  factDetails: Record<string, Record<string, string>> | undefined
): AffectedTag[] {
  const out: AffectedTag[] = [];
  for (const factId of factIds) {
    const info = tagInfoMap.get(factId);
    if (!info) continue;
    out.push({
      tagId: factId,
      conceptQName: info.conceptQName,
      pageId: info.pageId,
      pageNumber: info.pageNumber,
      sectionSuperId: info.sectionSuperId,
      sectionName: info.sectionName,
      details: factDetails?.[factId],
    });
  }
  return out;
}

function findingToTag(finding: ValidationFinding, factIdMap: Map<string, AffectedPage>, tagInfoMap: Map<string, TagInfo>): ListItemsTableTag {
  const affectedPages = resolveAffectedPages(finding.factIds, factIdMap);
  const affectedTags = resolveAffectedTags(finding.factIds, tagInfoMap, finding.factDetails);
  return {
    id: finding.code,
    label: finding.tag ?? finding.code.split(/[.:]/).pop() ?? finding.code,
    type: finding.type,
    locationLabel: locationLabelFor(affectedPages),
    status: finding.severity,
    statusInfo: finding.message,
    affectedCount: finding.affectedCount,
    affectedPages,
    affectedTags,
    code: finding.code,
  };
}

function detailsLabel(category: ValidationCategory): string | undefined {
  const { errors, warnings } = category.summary;
  const parts: string[] = [];
  if (errors > 0) {
    parts.push(i18n('tag:validation.error', { count: errors }));
  }
  if (warnings > 0) {
    parts.push(i18n('tag:validation.warning', { count: warnings }));
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function categoryResultForUi(category: ValidationCategory): string {
  return category.result;
}

function formatRanAt(iso: string | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return iso;
  }
}

export const Export = ({ documentId, onTagDetailsClick }: ExportProps) => {
  const { t } = useTranslation('tag');

  const [sortBy, setSortBy] = useState<'desc' | 'asc'>('asc');
  const navigate = useNavigate();
  const { documentId: documentIdParam } = useDocumentParams();
  const { data: pDocument } = useGetDocumentBySuperId(documentId);
  const documentRevisionId = pDocument?.revisionId as string | undefined;
  const documentLocale = pDocument?.content?.locale ?? 'sv-SE';
  const documentSuperIdInt = parseInt(documentId, 10);
  const { data: tagsResponse } = useTagsByDocument(Number.isNaN(documentSuperIdInt) ? undefined : documentSuperIdInt, {
    include: ['metadata', 'concept'],
  });
  const { data: taxonomyConfig } = useDocumentTaxonomyConfig(Number.isNaN(documentSuperIdInt) ? undefined : documentSuperIdInt);
  const hasActiveTaxonomy = !!taxonomyConfig?.taxonomySelections?.some((t) => t.isActive);
  const factIdMap = useMemo(() => buildFactIdMap(tagsResponse?.tags), [tagsResponse]);
  const tagInfoMap = useMemo(() => buildTagInfoMap(tagsResponse?.tags), [tagsResponse]);
  const { download: handleDownloadReportPackage, isDownloading } = useDownloadReportPackage({
    documentSuperId: documentId,
    documentRevisionId,
    documentLocale,
  });
  const {
    run: handleRunValidation,
    isRunning,
    result: runResult,
  } = useRunValidation({
    documentSuperId: documentId,
    documentRevisionId,
  });
  const { data: latestValidation } = useLatestValidation(Number.isNaN(documentSuperIdInt) ? undefined : documentSuperIdInt);
  const result = latestValidation?.latest?.result ?? runResult;
  const isValidating = isRunning || !!latestValidation?.running;
  const [detailsModal, setDetailsModal] = useState<{ show: boolean; finding: FindingDetails | null }>({
    show: false,
    finding: null,
  });

  const openFindingDetails = (tag: ListItemsTableTag) => {
    setDetailsModal({
      show: true,
      finding: {
        title: tag.label,
        type: tag.type,
        status: tag.status,
        message: tag.statusInfo,
        code: tag.code ?? tag.id,
        affectedCount: tag.affectedCount,
        affectedPages: tag.affectedPages,
        affectedTags: tag.affectedTags,
      },
    });
  };
  const closeFindingDetails = () => setDetailsModal({ show: false, finding: null });

  const handleNavigateToPage = (page: AffectedPage) => {
    navigate({
      to: '/documents/$documentId/sections/$sectionId',
      params: { documentId: documentIdParam, sectionId: String(page.sectionSuperId) },
      search: { taggingMode: true },
      hash: page.pageId ?? undefined,
    });
    closeFindingDetails();
  };

  const handleNavigateToTag = (tag: AffectedTag) => {
    navigate({
      to: '/documents/$documentId/sections/$sectionId',
      params: { documentId: documentIdParam, sectionId: String(tag.sectionSuperId) },
      search: { taggingMode: true },
      hash: tag.pageId ?? undefined,
    });
    closeFindingDetails();
  };

  const validationStatus = result?.status ?? 'not-run';

  const handleSortToggle = () => {
    setSortBy((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const handleRunTest = () => {
    console.log(`Run test clicked`);
  };

  const sortedCategories = useMemo(
    () => (result?.categories ?? []).slice().sort((a, b) => (sortBy === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name))),
    [result, sortBy]
  );

  return (
    <div className="export">
      <Typography variant="body-8" fontWeight="semibold">
        {t('export.document.label')}
      </Typography>

      <div className="export__actions">
        <button
          className="btn btn-primary"
          onClick={handleDownloadReportPackage}
          disabled={isDownloading || !hasActiveTaxonomy}
          aria-busy={isDownloading}
          title={!hasActiveTaxonomy ? t('categories.noActiveTaxonomy') || 'No active taxonomy selected.' : undefined}
        >
          {isDownloading && <i className="fa-regular fa-spinner fa-spin me-2" aria-hidden="true" />}
          {t(isDownloading ? 'export.action.generating' : 'export.action.generate')}
        </button>
        <button className="btn btn-secondary" disabled={true}>
          {t('export.action.export')}
        </button>
      </div>

      <Typography variant="body-8" fontWeight="semibold">
        {t('validation.label')}
      </Typography>

      <div className="export__validation">
        <div className="export__validation--left-container">
          <span className={`export__validation--status export__validation--${validationStatus}`}>{t(`validation.status.${validationStatus}`)}</span>
          <Typography variant="body-7" color="#59555F">
            {validationStatus === 'failed'
              ? t('validation.with.errors')
              : validationStatus === 'passed-with-warnings'
              ? t('validation.with.warnings')
              : validationStatus === 'passed'
              ? t('validation.no.warnings')
              : t('validation.not.run')}
          </Typography>
        </div>

        <div className="export__validation--right-container">
          <button className="export__validation--btn" onClick={handleRunValidation} disabled={isValidating} aria-busy={isValidating}>
            {isValidating && <i className="fa-regular fa-spinner fa-spin me-2" aria-hidden="true" />}
            {t('export.action.validate')}
          </button>
          {result && (
            <Typography variant="body-5" color="#59555F">
              {t('validation.last.checked')} {formatRanAt(result.ranAt)}
            </Typography>
          )}
        </div>
      </div>

      <section className="export-report">
        <div className="export-report__header">
          <div className="export-report__header--with-sort" onClick={handleSortToggle}>
            <Typography variant="body-5" fontWeight="bold" color="#547BA2" className="caps">
              {t('validation.check')}
            </Typography>
            <i className={`fa-solid fa-arrow-down ${sortBy === 'desc' ? 'rotate-180' : ''}`} />
          </div>
          <Typography variant="body-5" fontWeight="bold" color="#547BA2" className="caps">
            {t('validation.result')}
          </Typography>
          <Typography variant="body-5" fontWeight="bold" color="#547BA2" className="caps">
            {t('validation.details')}
          </Typography>
        </div>

        <Accordion flush>
          {sortedCategories.map((category, index) => (
            <Accordion.Item key={category.id} eventKey={String(index)}>
              <div className="export-report__item-header">
                <Accordion.Header>
                  <i className="fa-solid fa-lg fa-chevron-right export-report__chevron" />
                  <AccordionHeader title={category.name} result={categoryResultForUi(category)} details={detailsLabel(category)} />
                </Accordion.Header>
                <TagItemShortcuts actions={[{ type: 'test', onClick: handleRunTest }]} />
              </div>
              <Accordion.Body>
                <ListItemsTable
                  tags={category.findings.map((f) => findingToTag(f, factIdMap, tagInfoMap))}
                  onTagDetailsClick={onTagDetailsClick}
                  onFindingDetailsClick={openFindingDetails}
                  onNavigateToPage={handleNavigateToPage}
                />
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </section>

      <FindingDetailsModal
        show={detailsModal.show}
        finding={detailsModal.finding}
        onClose={closeFindingDetails}
        onNavigateToPage={handleNavigateToPage}
        onNavigateToTag={handleNavigateToTag}
      />
    </div>
  );
};
